"use client";

// packages/eden-fade/src/components/Fade.jsx
import { forwardRef, useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import {
  spacing32,
  grey3,
  backgroundColorLevelCurrent,
  setAppearanceConstantLocal
} from "@churchofjesuschrist/eden-style-constants";
import { useHasOverflow } from "@churchofjesuschrist/eden-has-overflow";
import {
  deepMapChildren,
  mergeRefs
} from "@churchofjesuschrist/eden-utilities";
import { OverflowContext } from '../utils/context.js';
import Scrollable from './Scrollable.js';
import { jsx, jsxs } from "react/jsx-runtime";
var [fadeToColorDef, fadeToColor] = setAppearanceConstantLocal(
  "fade-to-color",
  {
    light: grey3,
    dark: backgroundColorLevelCurrent,
    prefix: "eden"
  }
);
var Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  overflow: hidden;
`;
var Mask = styled.span`
  ${fadeToColorDef};
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 2;
`;
var backgroundDef = fadeToColorDef;
var size = spacing32;
var getBackgroundGradient = (dir) => `linear-gradient(to ${dir}, transparent calc(100% - ${size}), ${fadeToColor()} 100%)`;
var topBackground = getBackgroundGradient("top");
var rightBackground = getBackgroundGradient("right");
var bottomBackground = getBackgroundGradient("bottom");
var leftBackground = getBackgroundGradient("left");
var FadeComponent = ({ children, renderAs, disableTop, disableBottom, ...props }, forwardedRef) => {
  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => {
    setHasMounted(true);
  }, []);
  const [overflowRef, { hasTop, hasBottom, hasRight, hasLeft }] = useHasOverflow();
  let hasScrollable = false;
  deepMapChildren(children, (child) => {
    if (child?.type === Scrollable || child?.type?.target === Scrollable) {
      hasScrollable = true;
    }
  });
  const getBackground = useCallback(
    () => ({
      background: [
        hasTop && !disableTop ? topBackground : "",
        hasRight ? rightBackground : "",
        hasBottom && !disableBottom ? bottomBackground : "",
        hasLeft ? leftBackground : ""
      ].filter((background) => !!background).join(",")
    }),
    [disableBottom, disableTop, hasBottom, hasLeft, hasRight, hasTop]
  );
  return /* @__PURE__ */ jsx(
    Container,
    {
      as: renderAs,
      ...props,
      ref: hasScrollable ? null : mergeRefs(overflowRef, forwardedRef),
      children: /* @__PURE__ */ jsxs(OverflowContext.Provider, { value: overflowRef, children: [
        hasMounted ? /* @__PURE__ */ jsx(Mask, { style: getBackground() }) : null,
        children
      ] })
    }
  );
};
var Fade = forwardRef(FadeComponent);
Fade.displayName = "Fade";
var Fade_default = Fade;
export {
  backgroundDef,
  bottomBackground,
  Fade_default as default,
  leftBackground,
  rightBackground,
  size,
  topBackground
};
